import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiSmartphone, FiRefreshCw, FiLock, FiDownload, FiTv, FiSliders,
  FiHeart, FiZap, FiCamera, FiShare2, FiCheckCircle
} = FiIcons;

const MemoriesTab = () => {
  const { t } = useLanguage();

  const steps = [
    { step: "01", title: t('solutionsPage.memories.steps.s1Title') || "Create Your Event", desc: t('solutionsPage.memories.steps.s1Desc') || "Set up your gallery in seconds with custom branding, themes, and personalized privacy controls." },
    { step: "02", title: t('solutionsPage.memories.steps.s2Title') || "Share the QR Code", desc: t('solutionsPage.memories.steps.s2Desc') || "Display your unique QR code at your venue, print on place cards, or share via digital invites." },
    { step: "03", title: t('solutionsPage.memories.steps.s3Title') || "Guests Scan & Upload", desc: t('solutionsPage.memories.steps.s3Desc') || "No apps needed. Guests scan with their phone camera to instantly upload high-res photos and videos." },
    { step: "04", title: t('solutionsPage.memories.steps.s4Title') || "Relive the Magic", desc: t('solutionsPage.memories.steps.s4Desc') || "Watch live slideshows during the event, then download original-quality archives forever." }
  ];

  const features = [
    {
      icon: FiSmartphone,
      title: t('solutionsPage.memories.features.f1Title') || "Zero-Friction Access",
      description: t('solutionsPage.memories.features.f1Desc') || "No apps, no logins, no friction. Guests simply scan a unique QR code and start sharing their perspective instantly from any mobile browser."
    },
    {
      icon: FiRefreshCw,
      title: t('solutionsPage.memories.features.f2Title') || "Real-Time Live Sync",
      description: t('solutionsPage.memories.features.f2Desc') || "Watch your event gallery grow in real-time. Photos appear instantly in the cloud, allowing for live slideshows that keep the energy high."
    },
    {
      icon: FiLock,
      title: t('solutionsPage.memories.features.f3Title') || "Private & Secure",
      description: t('solutionsPage.memories.features.f3Desc') || "Your memories stay yours. Create a closed, private social network where only invited guests can see, share, and interact with content."
    },
    {
      icon: FiDownload,
      title: t('solutionsPage.memories.features.f4Title') || "Original Quality Storage",
      description: t('solutionsPage.memories.features.f4Desc') || "Stop chasing low-res screenshots. Download every guest photo and video in its full, uncompressed original quality for professional use."
    },
    {
      icon: FiTv,
      title: t('solutionsPage.memories.features.f5Title') || "Live Memory Walls",
      description: t('solutionsPage.memories.features.f5Desc') || "Turn any TV or projector into a live engagement hub. Stream guest uploads in a beautiful, automated slideshow as the event unfolds."
    },
    {
      icon: FiSliders,
      title: t('solutionsPage.memories.features.f6Title') || "Custom Event Branding",
      description: t('solutionsPage.memories.features.f6Desc') || "Make it yours. Customize your gallery theme, colors, and custom domain to perfectly align with your event's unique branding and vibe."
    },
    {
      icon: FiHeart,
      title: t('solutionsPage.memories.features.f7Title') || "Guest Interactions",
      description: t('solutionsPage.memories.features.f7Desc') || "Enable likes and comments to turn your gallery into a vibrant community. Let guests connect over shared moments long after the event ends."
    },
    {
      icon: FiZap,
      title: t('solutionsPage.memories.features.f8Title') || "Instant Setup",
      description: t('solutionsPage.memories.features.f8Desc') || "Go live in under 2 minutes. Our intuitive dashboard makes it incredibly simple to launch your event and generate your first QR code."
    }
  ];

  const livePhotos = [
    { title: 'Main Keynote Stage', author: 'Elena R.', likes: 48, time: '2m ago', image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80&w=400' },
    { title: 'VIP Networking Terrace', author: 'Marcus T.', likes: 35, time: '5m ago', image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&q=80&w=400' },
    { title: 'Afterparty Acoustic Set', author: 'Chloe M.', likes: 62, time: '8m ago', image: 'https://images.unsplash.com/photo-1482440308425-276ad0f28b19?auto=format&fit=crop&q=80&w=400' }
  ];

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-purple-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiCamera} className="w-3.5 h-3.5 text-purple-600 animate-pulse" />
          <span>{t('solutionsPage.memories.badge') || "Private AI-Powered Social Galleries"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.memories.title') || "The Private Social Network for Your Events"}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.memories.subtitle') || "The private social network for your events. Capture and share every moment with AI-powered galleries."}
        </p>
      </div>

      {/* Interactive QR & Live Gallery Showcase */}
      <div className="glass-panel rounded-[28px] p-6 md:p-8 border border-white/80 shadow-xl shadow-slate-200/40 inner-glow relative overflow-hidden space-y-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-slate-200/70">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-500 animate-pulse" />
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{t('solutionsPage.memories.galleryTitle') || "Live Event Gallery & Memory Wall"}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('solutionsPage.memories.gallerySubtitle') || "Guests snap and upload from any phone browser — no app installation required."}</p>
          </div>
          <span className="px-3.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-bold font-mono">
            {t('solutionsPage.memories.originalQuality') || "Original Uncompressed Quality"}
          </span>
        </div>

        {/* Live Photo Grid & QR Code Card */}
        <div className="grid lg:grid-cols-12 gap-6 items-center">
          {/* QR Code Hero Scanner (4 Cols) */}
          <div className="lg:col-span-4 p-6 rounded-2xl bg-white border border-slate-200/80 shadow-md text-center space-y-4">
            <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100">
              {t('solutionsPage.memories.scanBadge') || "Scan with Phone Camera"}
            </span>
            <div className="w-36 h-36 mx-auto bg-slate-900 rounded-2xl p-2.5 shadow-inner flex items-center justify-center">
              {/* QR Pattern Representation */}
              <div className="w-full h-full border-2 border-dashed border-white/40 rounded-xl flex flex-col items-center justify-center text-white/80 text-[10px] font-mono space-y-1">
                <SafeIcon icon={FiCamera} className="w-6 h-6 text-purple-400" />
                <span>{t('solutionsPage.memories.pointCamera') || "POINT CAMERA"}</span>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900">{t('solutionsPage.memories.zeroFrictionTitle') || "Zero-App Friction Upload"}</h4>
              <p className="text-[11px] text-slate-500 mt-0.5">{t('solutionsPage.memories.zeroFrictionDesc') || "Works on Safari, Chrome, Samsung Internet & more."}</p>
            </div>
          </div>

          {/* Real-Time Live Feed Sample (8 Cols) */}
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
            {livePhotos.map((photo, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -4 }}
                className="rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm space-y-2 pb-3"
              >
                <div className="h-32 w-full overflow-hidden relative">
                  <img
                    src={photo.image}
                    alt={photo.title}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-md text-[10px] text-white font-medium">
                    {photo.time}
                  </span>
                </div>
                <div className="px-3">
                  <h5 className="text-xs font-bold text-slate-900 truncate">{photo.title}</h5>
                  <div className="flex items-center justify-between mt-1 text-[11px] text-slate-500">
                    <span>{photo.author}</span>
                    <span className="flex items-center gap-1 text-purple-600 font-bold">
                      <SafeIcon icon={FiHeart} className="w-3 h-3 fill-purple-600" />
                      {photo.likes}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 4-Step Process Strip */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-slate-200/70">
          {steps.map((st) => (
            <div key={st.step} className="p-4 rounded-xl bg-white/60 border border-slate-200/80 space-y-1.5">
              <span className="text-xs font-extrabold text-purple-600 font-mono">{st.step}</span>
              <h4 className="text-xs font-bold text-slate-900">{st.title}</h4>
              <p className="text-[11px] text-slate-500 leading-relaxed font-normal">{st.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* The 8 Core Features Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {features.map((feat, idx) => (
          <motion.div
            key={idx}
            whileHover={{ y: -3 }}
            className="glass-card rounded-2xl p-5 border border-white/80 shadow-sm shadow-slate-100/60 hover:shadow-lg hover:border-purple-200/80 transition-all flex flex-col justify-between"
          >
            <div className="space-y-2.5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-600/20 border border-purple-200/60 flex items-center justify-center text-purple-600 shadow-sm">
                <SafeIcon icon={feat.icon} className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 tracking-tight">{feat.title}</h3>
              <p className="text-xs text-slate-600 leading-relaxed font-normal">{feat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default MemoriesTab;
